// Small building blocks for menus: elements, buttons, toasts, pop-ups.
import { isTouchScreen, pinchable } from './touch.ts';

type Child = Node | string | null | undefined | false;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Record<string, unknown> = {},
  ...children: (Child | Child[])[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(props)) {
    if (value === undefined || value === null || value === false) continue;
    if (key === 'class') node.className = String(value);
    else if (key === 'text') node.textContent = String(value);
    else if (key.startsWith('on') && typeof value === 'function')
      node.addEventListener(key.slice(2).toLowerCase(), value as EventListener);
    else if (key === 'style' && typeof value === 'object') {
      // custom properties ("--c1") need setProperty: assigning them like other styles does nothing
      for (const [k, v] of Object.entries(value as Record<string, string>)) {
        if (k.startsWith('--')) node.style.setProperty(k, v);
        else (node.style as unknown as Record<string, string>)[k] = v;
      }
    }
    else if (value === true) node.setAttribute(key, '');
    else node.setAttribute(key, String(value));
  }
  for (const child of children.flat()) {
    if (child === null || child === undefined || child === false) continue;
    node.append(child);
  }
  return node;
}

export function button(label: string, onClick: (e: MouseEvent) => void, cls = '', extra: Record<string, unknown> = {}) {
  return el('button', { class: `btn ${cls}`.trim(), type: 'button', onclick: onClick, ...extra }, label);
}

// ---------------- toasts ----------------

let toastHost: HTMLElement | null = null;
export function toast(message: string, kind: 'info' | 'error' | 'ok' = 'info') {
  if (!toastHost || !toastHost.isConnected) {
    toastHost = el('div', { class: 'toasts', role: 'status', 'aria-live': 'polite' });
    document.body.append(toastHost);
  }
  const t = el('div', { class: `toast toast-${kind}` }, message);
  toastHost.append(t);
  setTimeout(() => t.classList.add('toast-out'), 3200);
  setTimeout(() => t.remove(), 3700);
}

// ---------------- modal pop-ups ----------------

const openCloseFns: (() => void)[] = [];

export function modal(
  content: Node,
  opts: { onClose?: () => void; wide?: boolean; dismissable?: boolean; className?: string } = {},
) {
  const dismissable = opts.dismissable ?? true;
  const panel = el('div', { class: `modal ${opts.wide ? 'modal-wide' : ''} ${opts.className ?? ''}`, role: 'dialog' }, content);
  const backdrop = el('div', { class: 'backdrop' }, panel);
  let closed = false;
  const close = () => {
    if (closed) return;
    closed = true;
    backdrop.remove();
    const i = openCloseFns.indexOf(close);
    if (i >= 0) openCloseFns.splice(i, 1);
    opts.onClose?.();
  };
  if (dismissable) {
    backdrop.addEventListener('pointerdown', (e) => {
      if (e.target === backdrop) close();
    });
    openCloseFns.push(close);
  }
  backdrop.addEventListener('contextmenu', (e) => e.preventDefault());
  document.body.append(backdrop);
  return close;
}

export function closeTopModal(): boolean {
  const close = openCloseFns.at(-1);
  if (!close) return false;
  close();
  return true;
}

export function anyModalOpen() {
  return document.querySelector('.backdrop, .float-menu') !== null;
}

window.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') {
    if (closeFloatMenu()) return;
    closeTopModal();
  }
});

// ---------------- small floating menu (right-click menus) ----------------

let floatMenu: HTMLElement | null = null;
let floatMenuOnClose: (() => void) | undefined;

export interface MenuItem {
  label: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
  title?: string; // a hover hint (used by the buttons beside an enlarged card)
}

export function floatingMenu(x: number, y: number, title: string | null, items: (MenuItem | Node)[], onClose?: () => void) {
  closeFloatMenu();
  const menu = el(
    'div',
    { class: 'float-menu', role: 'menu' },
    title ? el('div', { class: 'float-title' }, title) : null,
    items.map((item) =>
      item instanceof Node
        ? item
        : el(
            'button',
            {
              class: `float-item ${item.danger ? 'danger' : ''}`,
              type: 'button',
              role: 'menuitem',
              disabled: item.disabled,
              onclick: () => {
                // a choice was made, so this isn't a cancel: skip the menu's on-close callback
                floatMenuOnClose = undefined;
                closeFloatMenu();
                item.onClick();
              },
            },
            item.label,
          ),
    ),
  );
  menu.addEventListener('contextmenu', (e) => e.preventDefault());
  document.body.append(menu);
  const r = menu.getBoundingClientRect();
  menu.style.left = `${Math.min(x, window.innerWidth - r.width - 8)}px`;
  menu.style.top = `${Math.min(y, window.innerHeight - r.height - 8)}px`;
  floatMenu = menu;
  floatMenuOnClose = onClose;
  setTimeout(() => window.addEventListener('pointerdown', outsideClick, true), 0);
  return menu;
}

function outsideClick(e: PointerEvent) {
  if (floatMenu && !floatMenu.contains(e.target as Node)) closeFloatMenu();
}

export function closeFloatMenu(): boolean {
  window.removeEventListener('pointerdown', outsideClick, true);
  if (!floatMenu) return false;
  floatMenu.remove();
  floatMenu = null;
  const cb = floatMenuOnClose;
  floatMenuOnClose = undefined;
  cb?.();
  return true;
}

// ---------------- large card view ----------------

/** A small colored tag above the card in its close-up: one for each token or effect on it ("WEAKNESS 2"). */
export interface CardBadge {
  label: string;
  color: string; // the tag's own color (the text is made dark or light to suit it)
  title?: string;
}

/** Is this color light enough to need dark text on it? */
function isLight(hex: string) {
  const h = hex.replace('#', '');
  const n = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const [r, g, b] = [0, 2, 4].map((i) => Number.parseInt(n.slice(i, i + 2), 16) || 0);
  return 0.299 * r + 0.587 * g + 0.114 * b > 150;
}

/**
 * A card, large. `extra.badges`: tags shown above the card's top-left corner, one per effect on it.
 * `extra.alerts`: short lines spelled out beside the card ("Deals 2 less damage (Weakness)").
 * `extra.counters`: the counters on the card and their numbers, listed beside it (Thorns 2, Buffer 3…).
 */
export function showCardLarge(src: Promise<string>, caption: string, note = '', actions: MenuItem[] = [], extra: { badges?: CardBadge[]; alerts?: string[]; counters?: { label: string; value: number; color: string }[]; rules?: Node | null; controls?: Node | null } = {}) {
  const img = el('img', { class: 'card-large', alt: caption, draggable: 'false' });
  src.then((s) => (img.src = s));
  const frame = el('div', { class: 'card-zoom', title: 'Scroll to zoom in' }, img);
  enableZoom(frame, img);
  let close = () => {};
  // the card takes the full height of the window; its name, notes and buttons sit beside it
  const badges = extra.badges ?? [];
  const alerts = extra.alerts ?? [];
  const tags = badges.length
    ? el(
        'div',
        { class: 'card-badges', 'aria-label': 'Effects on this card' },
        badges.map((b) => el('span', { class: `card-badge ${isLight(b.color) ? 'on-light' : ''}`, style: `--badge: ${b.color}`, title: b.title ?? '' }, b.label)),
      )
    : null;
  const content = el(
    'div',
    { class: `card-view ${tags ? 'has-badges' : ''}` },
    // the effects on the card sit above its top-left corner
    el('div', { class: 'card-view-main' }, tags, frame),
    el(
      'div',
      { class: `card-view-side ${extra.rules ? 'has-rules' : ''}` },
      el('div', { class: 'card-view-caption' }, caption),
      // what the card says: its stats, its descriptor lines and its description
      extra.rules ?? null,
      alerts.length ? el('div', { class: 'card-view-alerts' }, alerts.map((a) => el('div', { class: 'card-view-alert' }, a))) : null,
      extra.counters?.length
        ? el(
            'div',
            { class: 'card-view-counters', 'aria-label': 'Counters on this card' },
            extra.counters.map((k) => el('div', { class: 'card-view-counter' }, el('span', { class: 'counter-dot', style: `background: ${k.color}` }), el('span', { class: 'counter-name' }, k.label), el('span', { class: 'counter-num' }, String(k.value)))),
          )
        : null,
      note ? el('div', { class: 'card-view-note' }, note) : null,
      extra.controls ?? null,
      el('div', { class: 'card-view-hint' }, isTouchScreen() ? 'Pinch to zoom · drag to move around · double-tap to reset' : 'Scroll to zoom · drag to move around · double-click to reset'),
      el(
        'div',
        { class: 'card-view-actions' },
        actions.map((a) =>
          button(
            a.label,
            () => {
              close();
              a.onClick();
            },
            a.danger ? 'btn-danger' : '',
            { disabled: a.disabled, title: a.title },
          ),
        ),
        button('Close', () => close(), 'btn-ghost'),
      ),
    ),
  );
  close = modal(content, { className: 'modal-card' });
  return close;
}

/** Scroll-wheel zoom on a picture inside a fixed frame: zooms toward the cursor, drag to pan. */
function enableZoom(frame: HTMLElement, img: HTMLElement, maxZoom = 4) {
  let scale = 1;
  let tx = 0;
  let ty = 0;
  const apply = () => {
    const w = frame.clientWidth;
    const h = frame.clientHeight;
    // never show empty space past the picture's edges
    tx = Math.min(0, Math.max(w - w * scale, tx));
    ty = Math.min(0, Math.max(h - h * scale, ty));
    img.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
    frame.classList.toggle('zoomed', scale > 1);
  };
  frame.addEventListener(
    'wheel',
    (e) => {
      e.preventDefault();
      const r = frame.getBoundingClientRect();
      const px = e.clientX - r.left;
      const py = e.clientY - r.top;
      const next = Math.min(maxZoom, Math.max(1, scale * Math.exp(-e.deltaY * (e.deltaMode === 1 ? 0.05 : 0.0015))));
      // keep the spot under the cursor where it is
      tx = px - ((px - tx) * next) / scale;
      ty = py - ((py - ty) * next) / scale;
      scale = next;
      apply();
    },
    { passive: false },
  );
  // touch screens: pinch to zoom (toward the point between the fingers) and slide with both fingers
  const pinching = pinchable(frame, (k, mx, my, dx, dy) => {
    const r = frame.getBoundingClientRect();
    const px = mx - r.left;
    const py = my - r.top;
    const next = Math.min(maxZoom, Math.max(1, scale * k));
    tx = px - ((px - tx) * next) / scale + dx;
    ty = py - ((py - ty) * next) / scale + dy;
    scale = next;
    apply();
  });
  // and a double-tap resets it, like a double-click
  let lastTap = 0;
  frame.addEventListener('pointerup', (e) => {
    if (e.pointerType !== 'touch') return;
    const now = Date.now();
    if (now - lastTap < 320) {
      scale = 1;
      tx = ty = 0;
      apply();
      lastTap = 0;
    } else lastTap = now;
  });
  frame.addEventListener('pointerdown', (e) => {
    if (e.button !== 0 || scale === 1 || pinching()) return;
    e.preventDefault();
    frame.setPointerCapture(e.pointerId);
    let lastX = e.clientX;
    let lastY = e.clientY;
    const move = (ev: PointerEvent) => {
      if (pinching()) return;
      tx += ev.clientX - lastX;
      ty += ev.clientY - lastY;
      lastX = ev.clientX;
      lastY = ev.clientY;
      apply();
    };
    const up = () => {
      frame.removeEventListener('pointermove', move);
      frame.removeEventListener('pointerup', up);
      frame.removeEventListener('pointercancel', up);
    };
    frame.addEventListener('pointermove', move);
    frame.addEventListener('pointerup', up);
    frame.addEventListener('pointercancel', up);
  });
  frame.addEventListener('dblclick', () => {
    scale = 1;
    tx = ty = 0;
    apply();
  });
}

export async function copyText(text: string, fallbackField?: HTMLTextAreaElement) {
  try {
    await navigator.clipboard.writeText(text);
    toast('Copied to your clipboard.', 'ok');
  } catch {
    if (fallbackField) {
      fallbackField.focus();
      fallbackField.select();
    }
    toast('Copy didn’t work here — the text is selected, press Ctrl+C.', 'info');
  }
}

// ---------------- files ----------------

/** Hands the player a text file to keep (a replay). */
export function downloadText(fileName: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
  const link = el('a', { href: url, download: fileName, style: { display: 'none' } });
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/** Lets the player choose a file from their computer and gives back its text (null if they cancel). */
export function pickTextFile(accept: string): Promise<{ name: string; text: string } | null> {
  return new Promise((done) => {
    const input = el('input', { type: 'file', accept, style: { display: 'none' } });
    input.addEventListener('change', async () => {
      const file = input.files?.[0];
      input.remove();
      done(file ? { name: file.name, text: await file.text() } : null);
    });
    input.addEventListener('cancel', () => {
      input.remove();
      done(null);
    });
    document.body.append(input);
    input.click();
  });
}
